<template>
  <div class="ft-page">
    <!-- Top navbar -->
    <div class="ft-nav">
      <div class="ft-nav__logo">
        <!-- Dark-neutral-inverted variant on the dark navbar — see the note in
             PageGuestHome.vue. -->
        <img :src="logoSrc" alt="taxPOD" class="ft-nav__logo-img" />
      </div>

      <div class="ft-nav__spacer"></div>

      <!-- Dark mode toggle — same control as the console navbar and the login
           page. A native `title` stands in for the console's v-tooltip: this
           page is inside a `zoom: 1.1` wrapper, and a detached Vuetify overlay
           is positioned from an unzoomed coordinate space. -->
      <button
        class="darkmode-toggle"
        type="button"
        :title="isDarkMode ? 'Light Mode' : 'Dark Mode'"
        :aria-label="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
        :aria-pressed="String(isDarkMode)"
        @click="toggleDarkMode()"
      >
        <v-icon size="20" :color="isDarkMode ? 'var(--app-warning)' : 'var(--app-text-muted)'">
          {{ isDarkMode ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent' }}
        </v-icon>
      </button>
    </div>

    <!-- Main content -->
    <div class="ft-main">
      <!-- Left panel -->
      <div class="ft-left">
        <div class="ft-left__bg">
          <div class="geo geo--1"></div>
          <div class="geo geo--2"></div>
          <div class="geo geo--3"></div>
          <div class="geo geo--4"></div>
        </div>
        <div class="ft-left__content">
          <h1 class="ft-left__title">
            Simplify Your<br />
            HRDC Claims,<br />
            From Submission<br />
            to Approval
          </h1>
        </div>
      </div>

      <!-- Right panel -->
      <div class="ft-right">
        <div class="ft-right__wave">
          <svg viewBox="0 0 600 800" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path class="ft-wave__path" d="M600 800V0C600 0 580 200 500 350C420 500 300 550 250 650C200 750 300 800 600 800Z"/>
            <path class="ft-wave__path ft-wave__path--soft" d="M600 800V100C600 100 560 300 480 420C400 540 320 580 280 680C240 780 350 800 600 800Z"/>
          </svg>
        </div>

        <!-- Card -->
        <div class="ft-card">
          <router-link :to="{ name: 'PageGuestHome' }" class="ft-card__back">
            <v-icon size="18" color="var(--app-text-muted)">mdi-arrow-left</v-icon>
            <span>Back to Login</span>
          </router-link>

          <div class="ft-card__icon-wrap">
            <v-icon size="28" color="var(--app-accent)">mdi-account-plus-outline</v-icon>
          </div>

          <h2 class="ft-card__title">First Time Login</h2>
          <p class="ft-card__subtitle">Enter your email address to verify your account and set up your password</p>

          <transition name="fade">
            <div v-if="api.isError" class="ft-alert ft-alert--error">
              <v-icon size="18" color="var(--app-danger-fg)" class="ft-alert__icon">mdi-alert-circle-outline</v-icon>
              <span>{{ api.error }}</span>
            </div>
          </transition>

          <!-- Email -->
          <div class="field">
            <label class="field__label">Email Address</label>
            <div class="field__input-wrap">
              <v-icon size="18" color="var(--app-text-faint)" class="field__icon">mdi-email-outline</v-icon>
              <input
                type="email"
                class="field__input field__input--icon"
                placeholder="name@email.com"
                v-model="form.email"
                @keyup.enter="fetch"
              />
            </div>
          </div>

          <!-- Button -->
          <button
            class="ft-btn"
            :disabled="api.isLoading"
            @click="fetch()"
          >
            <span v-if="!api.isLoading">Continue</span>
            <span v-else class="ft-btn__loading">
              <v-progress-circular indeterminate size="18" width="2" color="var(--app-login-btn-fg)"></v-progress-circular>
              Verifying...
            </span>
          </button>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import Api from "@/objects/api";
import { themeState, toggleDark } from "@/plugins/theme";

export default {
  props: {
    email: {
      type: String,
    },
  },
  computed: {
    // The app-wide theme singleton, same state the login page and the console
    // navbar toggle.
    isDarkMode() {
      return themeState.dark;
    },
    logoSrc() {
      return this.isDarkMode
        ? "/images/taxpod_logo_dark.png"
        : "/images/taxpod_logo.png";
    },
  },
  data: () => ({
    api: new Api(),
    form: {
      email: null,
    },
  }),
  created() {
    if (this.email) this.form.email = this.$_.clone(this.email);
    this.api.setMethod(`POST`);
    this.api.setUrl(`${this.$service.sso}/v1/en/auth/login/email/verify`);
    this.api.setCallbackCompleted((resp) => {
      this.$router.push({
        name: "PageGuestLoginFirstTimeSetPassword",
        params: {
          email: resp.email,
        },
      });
    });
  },
  methods: {
    fetch() {
      this.api.setParams(this.form);
      this.api.fetch();
    },
    toggleDarkMode() {
      toggleDark();
    },
  },
};
</script>

<style scoped>
/* ===== DARK MODE =====
   Hand-authored, like PageGuestHome.vue — see the long note there. The short
   version: the generated companion set only ever named the navbar, the card and
   the fields, so .ft-page / .ft-left / .ft-right / the wave kept their light
   values and dark mode rendered as a dark card on a bright page. The panel, geo
   shapes, wave and CTA gradient are themed at their BASE rule via --app-login-*
   and need no override here. Token references only — no literal hexes. */

.dark-mode .ft-page {
  background: var(--app-page);
}

.dark-mode .ft-nav {
  background: var(--app-navbar);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .darkmode-toggle:hover {
  background-color: var(--app-hover);
}

.dark-mode .ft-right {
  background: var(--app-page);
}

.dark-mode .ft-card {
  background: var(--app-card);
  border-color: var(--app-border);
  box-shadow: 0 2px 12px var(--app-shadow-mid),
    inset 0 1px 0 var(--app-inner-highlight);
}

.dark-mode .ft-card__back {
  color: var(--app-text-muted);
}

.dark-mode .ft-card__back:hover {
  color: var(--app-accent);
}

/* The pale accent tile behind the header icon: a solid #f0f7ff would be a white
   block on the card, so dark uses the translucent accent wash instead. */
.dark-mode .ft-card__icon-wrap {
  background: var(--app-accent-soft);
}

.dark-mode .ft-card__title {
  color: var(--app-text);
}

.dark-mode .ft-card__subtitle {
  color: var(--app-text-faint);
}

.dark-mode .ft-alert--error {
  background: var(--app-danger-bg);
  border-color: var(--app-danger-border);
  color: var(--app-danger-fg);
}

.dark-mode .field__label {
  color: var(--app-text);
}

/* A field sits one step ABOVE the card it is on — --app-card here would make
   each input a hole in the card rather than a target to click into. */
.dark-mode .field__input {
  background: var(--app-field);
  border-color: var(--app-field-border);
  color: var(--app-text);
}

.dark-mode .field__input::placeholder {
  color: var(--app-field-placeholder);
}

.dark-mode .field__input:focus {
  border-color: var(--app-accent);
  box-shadow: 0 0 0 3px var(--app-ring);
}

/* ===== end dark mode ===== */

.ft-page {
  /* `zoom: 1.1` below scales this box after layout, so a plain 100vh paints at
     110vh and the bottom tenth — the footer — falls off screen behind
     `overflow: hidden`. Dividing by the zoom makes the painted height 100vh. */
  height: calc(100vh / 1.1);
  display: flex;
  flex-direction: column;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  background: #f0f4f8;
  zoom: 1.1;
  overflow: hidden;
}

/* Navbar */
.ft-nav {
  height: 56px;
  background: #fff;
  display: flex;
  align-items: center;
  padding: 0 32px;
  border-bottom: 1px solid #e8eaed;
  flex-shrink: 0;
  z-index: 10;
}

.ft-nav__logo-img {
  height: 32px;
  object-fit: contain;
}

.ft-nav__spacer {
  flex: 1;
}

/* Mirrors the console navbar's .darkmode-toggle so the control is the same
   object everywhere. */
.darkmode-toggle {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;
}

.darkmode-toggle:hover {
  background-color: rgba(0, 0, 0, 0.06);
}

/* Main */
.ft-main {
  flex: 1;
  display: flex;
  min-height: 0;
}

/* Left */
.ft-left {
  flex: 0 0 42%;
  background: var(--app-login-panel);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.ft-left__bg { position: absolute; inset: 0; }

.geo { position: absolute; background: var(--app-login-geo); }
.geo--1 { width: 300px; height: 300px; top: -50px; left: -80px; transform: rotate(45deg); border: 1px solid var(--app-login-geo-border); }
.geo--2 { width: 200px; height: 200px; top: 30%; left: 25%; transform: rotate(30deg); border: 1px solid var(--app-login-geo-border); }
.geo--3 { width: 250px; height: 250px; bottom: -60px; left: 10%; transform: rotate(60deg); border: 1px solid var(--app-login-geo-border); }
.geo--4 { width: 150px; height: 150px; top: 15%; right: -30px; transform: rotate(15deg); border: 1px solid var(--app-login-geo-border); }

.ft-left__content { position: relative; z-index: 1; padding: 60px; }
.ft-left__title { font-size: 1.8rem; font-weight: 800; color: var(--app-login-panel-fg); line-height: 1.35; margin: 0; }

/* Right */
.ft-right {
  flex: 1;
  background: #f0f4f8;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 40px;
}

.ft-right__wave { position: absolute; right: 0; bottom: 0; width: 60%; height: 100%; pointer-events: none; }
.ft-right__wave svg { width: 100%; height: 100%; }

/* Painted from CSS rather than a `fill` attribute so the wave follows the
   theme like everything else. */
.ft-wave__path { fill: var(--app-login-wave); }
.ft-wave__path--soft { fill: var(--app-login-wave-soft); }

/* Card */
.ft-card {
  width: 100%;
  max-width: 400px;
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
  border: 1px solid #e4e8ec;
  position: relative;
  z-index: 1;
}

.ft-card__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  text-decoration: none;
  margin-bottom: 24px;
  transition: color 0.15s;
}

.ft-card__back:hover { color: #0ea5e9; }

.ft-card__icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: #f0f7ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
}

.ft-card__title { font-size: 1.3rem; font-weight: 700; color: #1e293b; margin: 0 0 6px; }
.ft-card__subtitle { font-size: 0.82rem; color: #94a3b8; margin: 0 0 24px; font-weight: 400; line-height: 1.5; }

/* Alert */
.ft-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 0.82rem;
  font-weight: 500;
}

.ft-alert--error { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; }
.ft-alert__icon { flex-shrink: 0; }

/* Field */
.field { margin-bottom: 24px; }
.field__label { display: block; font-size: 0.82rem; font-weight: 600; color: #334155; margin-bottom: 6px; }
.field__input-wrap { position: relative; }
.field__icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); pointer-events: none; }

.field__input {
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.88rem;
  font-family: 'Lato', sans-serif;
  color: #1e293b;
  background: #fff;
  transition: all 0.2s ease;
  outline: none;
}

.field__input--icon { padding-left: 42px; }
.field__input::placeholder { color: #b0b8c1; }
.field__input:focus { border-color: #60b8e0; box-shadow: 0 0 0 3px rgba(96,184,224,0.1); }

/* Button */
.ft-btn {
  width: 100%;
  padding: 13px;
  background: var(--app-login-btn);
  color: var(--app-login-btn-fg);
  border: none;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 700;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ft-btn:hover { background: var(--app-login-btn-hover); box-shadow: 0 4px 14px var(--app-login-btn-glow); }
.ft-btn:disabled { opacity: 0.7; cursor: wait; }
.ft-btn__loading { display: flex; align-items: center; justify-content: center; gap: 10px; }

/* Mobile */
@media (max-width: 768px) {
  .ft-main { flex-direction: column; }
  .ft-left { flex: none; min-height: 160px; }
  .ft-left__content { padding: 32px 28px; }
  .ft-left__title { font-size: 1.2rem; }
  .ft-right { padding: 28px 20px; }
  .ft-card { max-width: 100%; padding: 24px; }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .ft-left { flex: 0 0 36%; }
  .ft-left__content { padding: 40px; }
  .ft-left__title { font-size: 1.4rem; }
}
</style>
